import { TransformationTypes } from "@/features/adp/types/transformation.types";


function toTwoDigitSuffix(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/**
 * Shape of a single item inside `entry.data.data[]` for any transformation.
 */
interface TransformationDataItem {
  id: string;
  label: string;
  formData: Record<string, unknown>;
}

/**
 * Shape of a transformation entry as stored by `useTransformationData`.
 * `data` is the `TransformationPayload` written by the transformation sheet.
 */
export interface StoredTransformationEntry {
  data: Record<string, unknown>;
}

/**
 * Mutable counter shared across all per-type builders so that the "first
 * transformation overall" rule is enforced globally, not per-type.
 */
interface BuilderState {
  /** Total number of transformation rows emitted so far (across all types). */
  totalEmitted: number;
  /** The real source table name to use for the very first row. */
  defaultSourceTable: string;
}

function asTransformationDataItems(value: unknown): TransformationDataItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is TransformationDataItem =>
      Boolean(item) &&
      typeof item === "object" &&
      typeof (item as Record<string, unknown>).id === "string" &&
      typeof (item as Record<string, unknown>).formData === "object",
  );
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => asString(item)).filter((item) => item.length > 0);
}

/**
 * Returns the correct SourceTable for the current row.
 * Only the very first row emitted (totalEmitted === 0) gets the real table name;
 * every subsequent row gets "output".
 */
function resolveSourceTable(state: BuilderState): string {
  return state.totalEmitted === 0
    ? state.defaultSourceTable || "output"
    : "output";
}

// ─── Per-transformation builders ──────────────────────────────────────────────

/**
 * Builds `FR01`, `FR02`, … entries for Find and Replace transformations.
 *
 * Only the very first item across ALL transformation types uses the real
 * source table name; all subsequent items use `"output"`.
 */
function buildFindAndReplaceEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.FindAndReplace}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        ReplaceInColumn: asString(formData.ReplaceInColumn),
        ReplaceWhat: asString(formData.ReplaceWhat),
        ReplaceWith: asString(formData.ReplaceWith),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Replace Nulls (RN) builder ───────────────────────────────────────────────

/**
 * Builds `RN01`, `RN02`, … entries for Replace Nulls transformations.
 *
 * Expected formData keys: `column_name`, `mode` (`"value"` | `"method"`),
 * `replacenullwith`, `method`.
 *
 * When `mode === "value"`, `replacenullwith` is the literal replacement string
 * and `method` is `null`. When `mode === "method"`, `method` is the aggregation
 * method and `replacenullwith` is `null`.
 *
 * Value shape matches FR/FL/FF: each key maps to a JSON-stringified array of
 * one object.
 */
function buildReplaceNullsEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.ReplaceNulls}${suffix}`;
    const formData = item.formData;
    const mode = asString(formData.mode);

    const replacenullwith =
      mode === "value" ? asString(formData.replacenullwith) : null;
    const method =
      mode === "method" ? asString(formData.method) : null;

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        column_name: asString(formData.column_name),
        replacenullwith,
        method,
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Filter (FL) builder ──────────────────────────────────────────────────────

/**
 * Builds `FL01`, `FL02`, … entries for Filter transformations.
 *
 * Expected formData keys: `SqlWhereClause`.
 *
 * Only the very first row across ALL transformation types uses the real
 * source table name; all subsequent rows use `"output"`.
 */
function buildFilterEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Filter}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        SqlWhereClause: asString(formData.SqlWhereClause),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Drop Rows (DR) builder ───────────────────────────────────────────────────

/**
 * Builds `DR01`, `DR02`, … entries for Drop Rows transformations.
 *
 * Expected formData keys: `SqlWhereClause`.
 *
 * Output matches Filter-style payload: JSON-stringified array of one object.
 */
function buildDropRowsEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.DropRows}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        SqlWhereClause: asString(formData.SqlWhereClause),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Forward Fill (FF) builder ────────────────────────────────────────────────

/**
 * Builds `FF01`, `FF02`, … entries for Forward Fill transformations.
 *
 * Expected formData keys: `ForwardFillOnColumn`, `GroupBy`, `OrderBy`.
 *
 * Only the very first row across ALL transformation types uses the real
 * source table name; all subsequent rows use `"output"`.
 */
function buildForwardFillEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.ForwardFill}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        ForwardFillOnColumn: asString(formData.ForwardFillOnColumn),
        GroupBy: asString(formData.GroupBy),
        OrderBy: asString(formData.OrderBy),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Case Set (CS) builder ────────────────────────────────────────────────────

/**
 * Builds `CS01`, `CS02`, … entries for Case Set transformations.
 *
 * Expected formData keys: `case_change_columns` (string[]), `case` (string).
 *
 * Output shape:
 * ```json
 * { "CS01": { "SourceTable": "payment", "case_change_columns": ["amount"], "case": "lower" } }
 * ```
 */
function buildCaseSetEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.CaseSet}${suffix}`;
    const formData = item.formData;

    const caseChangeColumns = Array.isArray(formData.case_change_columns)
      ? (formData.case_change_columns as unknown[]).map((c) => asString(c))
      : [];

    result[key] = {
      SourceTable: resolveSourceTable(state),
      case_change_columns: caseChangeColumns,
      case: asString(formData.case),
    };

    state.totalEmitted += 1;
  });

  return result;
}

// ─── Data Type Conversion (DC) builder ───────────────────────────────────────

/**
 * Builds `DC01`, `DC02`, … entries for Data Type Conversion transformations.
 *
 * Each formData item produces one key. The value is a plain object (not a
 * stringified array) with `SourceTable` and `dataTypeConversion_zip`.
 *
 * Expected formData keys: `columnName`, `targetDataType`.
 *
 * Output shape:
 * ```json
 * { "DC01": { "SourceTable": "payment", "dataTypeConversion_zip": [["amount", "boolean"]] } }
 * ```
 */
function buildDataTypeConversionEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.DataTypeConversion}${suffix}`;
    const formData = item.formData;

    result[key] = {
      SourceTable: resolveSourceTable(state),
      dataTypeConversion_zip: [[
        asString(formData.columnName),
        asString(formData.targetDataType),
      ]],
    };

    state.totalEmitted += 1;
  });

  return result;
}

// ─── Rename Column (RC) builder ───────────────────────────────────────────────

/**
 * Builds `RC01`, `RC02`, … entries for Rename Column transformations.
 *
 * Expected formData keys: `oldColumn`, `newColumn`.
 *
 * Output shape:
 * ```json
 * { "RC01": { "SourceTable": "output", "renameColumnsList": [["address", "emp_address"]] } }
 * ```
 */
function buildRenameColumnEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.RenameColumn}${suffix}`;
    const formData = item.formData;

    result[key] = {
      SourceTable: resolveSourceTable(state),
      renameColumnsList: [[
        asString(formData.oldColumn),
        asString(formData.newColumn),
      ]],
    };

    state.totalEmitted += 1;
  });

  return result;
}

// ─── Sort (SR) builder ───────────────────────────────────────────────────────

/**
 * Builds `SR01`, `SR02`, … entries for Sort transformations.
 *
 * Expected formData keys: `sort_columns` (string[]), `ascending` (string).
 *
 * Output shape:
 * ```json
 * { "SR01": { "SourceTable": "output", "sort_columns": ["created_dt"], "ascending": "true" } }
 * ```
 */
function buildSortEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Sort}${suffix}`;
    const formData = item.formData;

    result[key] = {
      SourceTable: resolveSourceTable(state),
      sort_columns: asStringArray(formData.sort_columns),
      ascending: asString(formData.ascending),
    };

    state.totalEmitted += 1;
  });

  return result;
}

// ─── Drop Duplicates (DP) builder ────────────────────────────────────────────

/**
 * Builds `DP01`, `DP02`, … entries for Drop Duplicates transformations.
 *
 * Expected formData keys: `entireRow` ("Yes" | "No"), `onColumns` (string[]).
 *
 * Rules:
 * - entireRow === "Yes"  -> onColumns: ""
 * - entireRow === "No"   -> onColumns: comma-joined list
 */
function buildDropDuplicatesEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.DropDuplicates}${suffix}`;
    const formData = item.formData;

    const entireRow = asString(formData.entireRow);
    const isEntireRow = entireRow.toLowerCase() === "yes";

    result[key] = {
      SourceTable: resolveSourceTable(state),
      entireRow,
      onColumns: isEntireRow ? "" : asStringArray(formData.onColumns).join(","),
    };

    state.totalEmitted += 1;
  });

  return result;
}

// ─── Replace Mapping (RM) builder ────────────────────────────────────────────

/**
 * Builds `RM01`, `RM02`, … entries for Replace Mapping transformations.
 *
 * Expected formData keys (supports aliases):
 * - source table: `SourceTable`
 * - map column: `mapColumn`
 * - map value: `mapValue` | `mapValueInColumn`
 * - replace column: `replaceColumn` | `replaceInColumn`
 * - replace value: `replaceValue` | `replaceWithValue`
 *
 * Output shape:
 * ```json
 * { "RM01": "{\"SourceTable\":\"emp_bucket\",\"replaceMappingList\":[[\"address\",\"something\",\"gender\",\"male\"]]}" }
 * ```
 */
function buildReplaceMappingEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.ReplaceMapping}${suffix}`;
    const formData = item.formData;

    const definition = {
      SourceTable: resolveSourceTable(state),
      replaceMappingList: [[
        asString(formData.mapColumn),
        asString(formData.mapValue) || asString(formData.mapValueInColumn),
        asString(formData.replaceColumn) || asString(formData.replaceInColumn),
        asString(formData.replaceValue) || asString(formData.replaceWithValue),
      ]],
    };

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Split (SP) builder ───────────────────────────────────────────────────────

/**
 * Builds `SP01`, `SP02`, … entries for Split transformations.
 *
 * Expected formData keys: `Column`, `Separator`, `NewColumns`.
 *
 * Output shape:
 * ```json
 * {
 *   "SP01": "[{\"TransformationNo\":\"SP01\",\"SourceTable\":\"output\",\"Column\":\"gender\",\"Separator\":\"_\",\"NewColumns\":\"firstname,lastname\"}]"
 * }
 * ```
 */
function buildSplitEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Split}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        Column: asString(formData.Column),
        Separator: asString(formData.Separator),
        NewColumns: asString(formData.NewColumns),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Concat (CC) builder ─────────────────────────────────────────────────────

/**
 * Builds `CC01`, `CC02`, … entries for Concat transformations.
 *
 * Expected formData keys: `New_ColumnName`, `FirstColumn`, `SecondColumn`, `ConcatWith`.
 *
 * Output shape:
 * ```json
 * {
 *   "CC01": "[{\"TransformationNo\":\"CC01\",\"SourceTable\":\"employee_details_adp\",\"New_ColumnName\":\"full_name_concat\",\"FirstColumn\":\"first_name\",\"SecondColumn\":\"last_name\",\"ConcatWith\":\"-\"}]"
 * }
 * ```
 */
function buildConcatEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Concat}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        New_ColumnName: asString(formData.New_ColumnName),
        FirstColumn: asString(formData.FirstColumn),
        SecondColumn: asString(formData.SecondColumn),
        ConcatWith: asString(formData.ConcatWith),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Lookup (LK) builder ──────────────────────────────────────────────────────

/**
 * Builds `LK01`, `LK02`, … entries for Lookup transformations.
 *
 * Expected formData keys: `SourceTable`, `SourceColumn`, `LookupTableName`,
 * `LookupTable_DataSource`, `LookUpColumns`, `LookUpGetColumns`.
 *
 * Payload shape follows backend expectation: each key maps to a JSON-stringified
 * array containing one object with lookup configuration details.
 */
function buildLookupEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Lookup}${suffix}`;
    const formData = item.formData;

    const lookupGetColumns = Array.isArray(formData.LookUpGetColumns)
      ? asStringArray(formData.LookUpGetColumns).join(",")
      : asString(formData.LookUpGetColumns);

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        SourceColumn: asString(formData.SourceColumn),
        LookupTableName: asString(formData.LookupTableName),
        LookupTable_DataSource: asString(formData.LookupTable_DataSource),
        LookupTableName_isMaster: "No",
        LookUpColumns: asString(formData.LookUpColumns),
        LookUpGetColumns: lookupGetColumns,
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Join (JR) builder ────────────────────────────────────────────────────────

/**
 * Builds `JR01`, `JR02`, … entries for Join integration nodes.
 *
 * Each `item.formData` is one `JoinRowFormData` (see `JoinIntegrationSheet`) —
 * exactly one item per secondary join table. Payload shape mirrors the legacy
 * Angular `prepareJSON` output (one JR per row, value is a single-element
 * JSON-stringified array).
 *
 * `SourceTable` is resolved via `resolveSourceTable(state)` so the first
 * globally-emitted row gets the real primary table name and every subsequent
 * row (within this Join node OR across other Join/Union nodes) gets `"output"`.
 * The priority sort in `buildTransformationDefinition` guarantees JR rows are
 * processed before non-priority transformations.
 */
function buildJoinEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Join}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        TransformationNo: key,
        JoinType: asString(formData.joinType),
        SourceTable: resolveSourceTable(state),
        SourceTableKey: asString(formData.primaryJoinKey),
        SourceTableGetColumns: asString(formData.primaryColumns),
        JoinTableName: asString(formData.joinTableName),
        JoinTable_DataSource: asString(formData.joinTableDataSource),
        JoinTable_isMaster: "No",
        JoinTableKey: asString(formData.joinTableJoinKey),
        JoinTableGetColumns: asString(formData.joinTableColumns),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Union (UN) builder ──────────────────────────────────────────────────────

/**
 * Builds `UN01`, `UN02`, … entries for Union integration nodes.
 *
 * Each `item.formData` is one `UnionRowFormData` (see `UnionIntegrationSheet`) —
 * exactly one item per secondary input table. Output shape mirrors the Angular
 * `prepareJSON()` in `source-destination/union/union.component.ts`:
 *
 * ```json
 * {
 *   "UN01": "[{\"TransformationNo\":\"UN01\",\"SourceTable\":\"customers\",\"WithTable\":\"orders\",\"WithTable_DataSource\":\"postgres_source_362\",\"WithTable_isMaster\":\"no\",\"WithDuplicates\":\"yes\",\"WithoutDuplicates\":\"no\",\"SortBy\":null}]"
 * }
 * ```
 *
 * `SourceTable` is resolved via `resolveSourceTable(state)` so the first
 * globally-emitted row gets the real primary table name and every subsequent
 * row (within this Union node OR across other Union/Join nodes) gets `"output"`.
 * The priority sort in `buildTransformationDefinition` guarantees UN rows are
 * processed before non-priority transformations.
 *
 * `WithDuplicates` / `WithoutDuplicates` are mirrors of each other (Angular
 * has both keys explicitly): yes/no toggles UNION ALL vs UNION DISTINCT.
 */
function buildUnionEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Union}${suffix}`;
    const formData = item.formData;

    const withDuplicates = asString(formData.withDuplicates).toLowerCase() === 'no' ? 'no' : 'yes';
    const sortByRaw = formData.sortBy;
    const sortBy = typeof sortByRaw === 'string' && sortByRaw.trim().length > 0 ? sortByRaw : null;

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        WithTable: asString(formData.withTableName),
        WithTable_DataSource: asString(formData.withTableDataSource),
        WithTable_isMaster: 'no',
        WithDuplicates: withDuplicates,
        WithoutDuplicates: withDuplicates === 'yes' ? 'no' : 'yes',
        SortBy: sortBy,
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Zscore (ZS) builder ──────────────────────────────────────────────────────

/**
 * Builds `ZS01`, `ZS02`, … entries for Zscore transformations.
 *
 * Expected formData keys: `SourceTable`, `zscore_on_column`, `new_column`.
 *
 * Output shape:
 * ```json
 * {
 *   "ZS01": "[{\"SourceTable\":\"employee_details_adp\",\"zscore_on_column\":\"emp_id\",\"new_column\":\"emp_num_id\"}]"
 * }
 * ```
 */
function buildZscoreEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Zscore}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        SourceTable: resolveSourceTable(state),
        zscore_on_column: asString(formData.zscore_on_column),
        new_column: asString(formData.new_column),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Box-Cox (BC) builder ─────────────────────────────────────────────────────

/**
 * Builds `BC01`, `BC02`, … entries for Box-Cox transformations.
 *
 * Expected formData keys: `SourceTable`, `column_name`, `new_column_name`,
 * `lamda_value`.
 *
 * Output shape:
 * ```json
 * {
 *   "BC01": "[{\"SourceTable\":\"employee_details_adp\",\"column_name\":\"performance_score\",\"new_column_name\":\"performance_box\",\"lamda_value\":\"0.5\"}]"
 * }
 * ```
 */
function buildBoxCoxEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.BoxCox}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        SourceTable: resolveSourceTable(state),
        column_name: asString(formData.column_name),
        new_column_name: asString(formData.new_column_name),
        lamda_value: asString(formData.lamda_value),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Drop Fields (DF) builder ─────────────────────────────────────────────────

/**
 * Builds `DF01`, `DF02`, … entries for Drop Fields transformations.
 *
 * Expected formData keys: `dropFieldsList` (string[]).
 *
 * Output shape:
 * ```json
 * { "DF01": "{\"SourceTable\":\"employee_details_adp\",\"dropFieldsList\":[\"attendance_pct\",\"city\"]}" }
 * ```
 */
function buildDropFieldsEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.DropFields}${suffix}`;
    const formData = item.formData;

    const definition = {
      SourceTable: resolveSourceTable(state),
      dropFieldsList: asStringArray(formData.dropFieldsList),
    };

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Date to Integer (DI) builder ────────────────────────────────────────────

/**
 * Builds `DI01`, `DI02`, … entries for Date to Integer transformations.
 *
 * Expected formData keys: `SourceTable`, `DateColumn`, `NewDateKeyColumnName`, `KeyType`.
 *
 * Output shape:
 * ```json
 * {
 *   "DI01": "[{\"TransformationNo\":\"DI01\",\"SourceTable\":\"output\",\"DateColumn\":\"dob\",\"NewDateKeyColumnName\":\"date_of_birth\",\"KeyType\":\"int\"}]"
 * }
 * ```
 */
function buildDateToIntegerEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.DateToInteger}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        DateColumn: asString(formData.DateColumn),
        NewDateKeyColumnName: asString(formData.NewDateKeyColumnName),
        KeyType: asString(formData.KeyType),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Date Split (DS) builder ──────────────────────────────────────────────────

/**
 * Builds `DS01`, `DS02`, … entries for Date Split transformations.
 *
 * Expected formData keys: `SourceTable`, `DateColumn`.
 *
 * Output shape:
 * ```json
 * {
 *   "DS01": "{\"SourceTable\":\"employee_details_adp\",\"DateColumn\":\"dob\"}"
 * }
 * ```
 */
function buildDateSplitEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.DateSplit}${suffix}`;
    const formData = item.formData;

    const definition = {
      SourceTable: resolveSourceTable(state),
      DateColumn: asString(formData.DateColumn),
    };

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Transpose (TP) builder ───────────────────────────────────────────────────

interface ColumnConfigRow {
  columnName?: unknown;
  condition?: unknown;
}

function partitionTransposeColumnConfig(
  formData: Record<string, unknown>,
): { fixed: string[]; ignore: string[]; transpose: string[] } {
  const raw = formData.columnConfig;
  if (!Array.isArray(raw)) {
    return { fixed: [], ignore: [], transpose: [] };
  }

  const fixed: string[] = [];
  const ignore: string[] = [];
  const transpose: string[] = [];

  for (const row of raw) {
    if (row == null || typeof row !== "object") continue;
    const r = row as ColumnConfigRow;
    const columnName = asString(r.columnName);
    if (!columnName) continue;

    const condition = asString(r.condition).toLowerCase();
    if (condition === "fixed") fixed.push(columnName);
    else if (condition === "ignore") ignore.push(columnName);
    else if (condition === "transpose") transpose.push(columnName);
  }

  return { fixed, ignore, transpose };
}

function asTransposeHeaderRows(value: unknown): { name: string; dataType: string }[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === "object",
    )
    .map((item) => ({
      name: asString(item.name),
      dataType: asString(item.dataType),
    }));
}

/**
 * Builds `TP01`, `TP02`, … entries for Transpose transformations.
 *
 * Expected formData keys: `columnConfig` (rows with `columnName`, `condition`:
 * `"fixed"` | `"ignore"` | `"transpose"`), `transposeHeaders` (`name`, `dataType`).
 *
 * Output shape (value is a JSON string):
 * ```json
 * {
 *   "TP01": "{\"SourceTable\":\"output\",\"fixed\":[\"a\"],\"ignore\":[\"b\"],\"transpose\":[\"c\"],\"transposeHeaders\":[{\"name\":\"h1\",\"dataType\":\"int\"}]}"
 * }
 * ```
 */
function buildTransposeEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Transpose}${suffix}`;
    const formData = item.formData;

    const { fixed, ignore, transpose } = partitionTransposeColumnConfig(formData);
    const transposeHeaders = asTransposeHeaderRows(formData.transposeHeaders);

    const definition = {
      SourceTable: resolveSourceTable(state),
      fixed,
      ignore,
      transpose,
      transposeHeaders,
    };

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── UDF (UF) builder ─────────────────────────────────────────────────────────

function formatUdfCode(value: unknown): string {
  return asString(value)
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .replaceAll(/ {4}/g, "\t")
    .replace(/\s+$/, "")
    .replaceAll("'", '"');
}

/**
 * Builds `UF01`, `UF02`, … entries for UDF transformations.
 *
 * Expected formData keys: `SourceTable`, `Code`.
 *
 * Output shape:
 * ```json
 * {
 *   "UF01": "[{\"SourceTable\":\"EQUIPMENT_OFFLINE\",\"Code\":\"def custom_function(df):\\n\\tdf[\\\"age\\\"] = df[\\\"age\\\"] + 10\\n\\treturn df\"}]"
 * }
 * ```
 */
function buildUdfEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.UDF}${suffix}`;
    const formData = item.formData;

    const definition = [
      {
        SourceTable: resolveSourceTable(state),
        Code: formatUdfCode(formData.Code),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Aggregation (AG) builder ─────────────────────────────────────────────────

/**
 * Builds `AG01`, `AG02`, … entries for Aggregation transformations.
 *
 * Expected formData keys: `groupByColumns` (string[]), `aggregateList` (group rows).
 * Each row in `aggregateList` has: `newColumnName`, `aggregateColumn`,
 * `aggregateType`, `percentileType`, `newDataType`.
 *
 * Output shape per applied item:
 * ```json
 * {
 *   "AG01": {
 *     "SourceTable": "emp_bucket",
 *     "aggregateList": [
 *       ["sum", ["address", "department_id"], "emp_height", "hello", null]
 *     ]
 *   }
 * }
 * ```
 * Each row tuple: `[aggregateType, [groupByColumns], aggregateColumn, newColumnName, percentileType|null]`
 */
function buildAggregationEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Aggregation}${suffix}`;
    const formData = item.formData;

    const groupByColumns = asStringArray(formData.groupByColumns);

    const rawAggList = Array.isArray(formData.aggregateList)
      ? (formData.aggregateList as unknown[])
      : [];

    const aggregateList = rawAggList.map((row) => {
      const r = (row != null && typeof row === "object")
        ? (row as Record<string, unknown>)
        : {} as Record<string, unknown>;
      const aggregateType = asString(r.aggregateType);
      const aggregateColumn = asString(r.aggregateColumn);
      const newColumnName = asString(r.newColumnName);
      const percentileType =
        aggregateType === "percentile"
          ? asString(r.percentileType) || null
          : null;
      return [aggregateType, groupByColumns, aggregateColumn, newColumnName, percentileType];
    });

    result[key] = {
      SourceTable: resolveSourceTable(state),
      aggregateList,
    };

    state.totalEmitted += 1;
  });

  return result;
}

// ─── Cumulative (CU) builder ──────────────────────────────────────────────────

/**
 * Builds `CU01`, `CU02`, … entries for Cumulative transformations.
 *
 * Expected formData keys: `SourceTable`, `CumulativeType`, `CumulateOnColumn`,
 * `NewColumnName`, `GroupBy` (string[]), `OrderBy`.
 *
 * `GroupBy` arrives as a list of column names and is serialised as a
 * comma+space joined string to match the backend payload.
 *
 * Output shape:
 * ```json
 * {
 *   "CU01": "[{\"TransformationNo\":\"CU01\",\"SourceTable\":\"emp_bucket\",\"CumulativeType\":\"sum\",\"CumulateOnColumn\":\"address\",\"NewColumnName\":\"cumulated_column\",\"GroupBy\":\"address, department_id\",\"OrderBy\":\"dob\"}]"
 * }
 * ```
 */
function buildCumulativeEntries(
  items: TransformationDataItem[],
  startIndex: number,
  state: BuilderState,
): Record<string, string> {
  const result: Record<string, string> = {};

  items.forEach((item, itemIndex) => {
    const suffix = toTwoDigitSuffix(startIndex + itemIndex);
    const key = `${TransformationTypes.Cumulative}${suffix}`;
    const formData = item.formData;

    const groupBy = Array.isArray(formData.GroupBy)
      ? asStringArray(formData.GroupBy).join(", ")
      : asString(formData.GroupBy);

    const definition = [
      {
        TransformationNo: key,
        SourceTable: resolveSourceTable(state),
        CumulativeType: asString(formData.CumulativeType),
        CumulateOnColumn: asString(formData.CumulateOnColumn),
        NewColumnName: asString(formData.NewColumnName),
        GroupBy: groupBy,
        OrderBy: asString(formData.OrderBy),
      },
    ];

    result[key] = JSON.stringify(definition);
    state.totalEmitted += 1;
  });

  return result;
}

// ─── Main builder
/**
 * Builds the full `transformation_definition` object from all upstream
 * transformation entries.
 *
 * **Source table rule:** only the very first transformation row emitted —
 * regardless of type — receives the real source table name. Every subsequent
 * row receives `"output"`. This mirrors the backend expectation when multiple
 * transformation types are chained (e.g. 2× FR followed by 3× FL).
 *
 * @param transformationEntries - All transformation entries retrieved via
 *   `useTransformationData().getTransformationDataByNodeId()` for each
 *   upstream transformation node id, in pipeline order.
 * @param defaultSourceTable - The real source table name (from the source node).
 *
 * @returns A flat `Record<string, unknown>` where each key is a transformation
 *   code (e.g. `"FR01"`, `"RN01"`, `"DC01"`) and each value is either a JSON-stringified
 *   array (FR, FL, FF, RN) or a plain object (DC, CS).
 */
export function buildTransformationDefinition(params: {
  transformationEntries: StoredTransformationEntry[];
  defaultSourceTable?: string;
}): Record<string, unknown> {
  const { transformationEntries, defaultSourceTable = "" } = params;
  const result: Record<string, unknown> = {};

  /** Shared mutable state — tracks how many rows have been emitted globally. */
  const state: BuilderState = { totalEmitted: 0, defaultSourceTable };

  /** Per-type running index so each type numbers its own keys independently. */
  const indexMap: Partial<Record<TransformationTypes, number>> = {};

  const getIndex = (type: TransformationTypes): number => {
    indexMap[type] ??= 1;
    return indexMap[type];
  };

  const advanceIndex = (type: TransformationTypes, by: number): void => {
    indexMap[type] = getIndex(type) + by;
  };

  type EntryBuilder = (
    items: TransformationDataItem[],
    startIndex: number,
    state: BuilderState,
  ) => Record<string, unknown>;

  const builders: Partial<Record<TransformationTypes, EntryBuilder>> = {
    [TransformationTypes.FindAndReplace]: buildFindAndReplaceEntries as EntryBuilder,
    [TransformationTypes.Filter]: buildFilterEntries as EntryBuilder,
    [TransformationTypes.ForwardFill]: buildForwardFillEntries as EntryBuilder,
    [TransformationTypes.ReplaceNulls]: buildReplaceNullsEntries as EntryBuilder,
    [TransformationTypes.DropRows]: buildDropRowsEntries as EntryBuilder,
    [TransformationTypes.RenameColumn]: buildRenameColumnEntries,
    [TransformationTypes.DataTypeConversion]: buildDataTypeConversionEntries,
    [TransformationTypes.CaseSet]: buildCaseSetEntries,
    [TransformationTypes.Sort]: buildSortEntries,
    [TransformationTypes.DropDuplicates]: buildDropDuplicatesEntries,
    [TransformationTypes.DropFields]: buildDropFieldsEntries as EntryBuilder,
    [TransformationTypes.ReplaceMapping]: buildReplaceMappingEntries as EntryBuilder,
    [TransformationTypes.Split]: buildSplitEntries as EntryBuilder,
    [TransformationTypes.Concat]: buildConcatEntries as EntryBuilder,
    [TransformationTypes.Lookup]: buildLookupEntries as EntryBuilder,
    [TransformationTypes.Join]: buildJoinEntries as EntryBuilder,
    [TransformationTypes.Union]: buildUnionEntries as EntryBuilder,
    [TransformationTypes.Zscore]: buildZscoreEntries as EntryBuilder,
    [TransformationTypes.BoxCox]: buildBoxCoxEntries as EntryBuilder,
    [TransformationTypes.DateToInteger]: buildDateToIntegerEntries as EntryBuilder,
    [TransformationTypes.DateSplit]: buildDateSplitEntries as EntryBuilder,
    [TransformationTypes.Transpose]: buildTransposeEntries as EntryBuilder,
    [TransformationTypes.UDF]: buildUdfEntries as EntryBuilder,
    [TransformationTypes.Cumulative]: buildCumulativeEntries as EntryBuilder,
    [TransformationTypes.Aggregation]: buildAggregationEntries,
  };

  /**
   * Integration transformations (Join `JR`, Union `UN`) must be processed
   * **before** every other transformation. They produce the merged "output"
   * table that subsequent transformations operate on, so they must own the
   * very first row (the one that gets the real `SourceTable` name).
   *
   * `Array.prototype.sort` is stable in modern JS engines, so non-priority
   * entries keep their original (canvas-derived) order relative to each other.
   */
  const PRIORITY_ABBREVIATIONS = new Set<TransformationTypes>([
    TransformationTypes.Join,
    TransformationTypes.Union,
  ]);
  const sortedEntries = [...transformationEntries].sort((a, b) => {
    const aPriority = PRIORITY_ABBREVIATIONS.has(asString(a.data.abbreviation) as TransformationTypes) ? 0 : 1;
    const bPriority = PRIORITY_ABBREVIATIONS.has(asString(b.data.abbreviation) as TransformationTypes) ? 0 : 1;
    return aPriority - bPriority;
  });

  for (const entry of sortedEntries) {
    const abbreviation = asString(entry.data.abbreviation) as TransformationTypes;
    const builder = builders[abbreviation];
    if (!builder) continue;

    const items = asTransformationDataItems(entry.data.data);
    if (items.length === 0) continue;

    Object.assign(result, builder(items, getIndex(abbreviation), state));
    advanceIndex(abbreviation, items.length);
  }

  return result;
}
